"use client";

import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  platform,
  type DesignPublishedSnapshot,
  type DesignWebsite,
  type DesignSaveOperation,
} from "./api";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "./ui";
import css from "./BuildyWorkspace.module.css";

/** Same published-snapshot API as the existing editor; no synthetic history entries. */
export function WebsiteVersionHistory({
  websiteId,
  org,
  previewOnly,
  hasUnsavedWork,
  onReturnEditor,
  pageName,
  canRestore = false,
  csrf,
}: {
  websiteId: string;
  org: number;
  previewOnly: boolean;
  hasUnsavedWork: boolean;
  onReturnEditor?: () => void;
  pageName?: string;
  canRestore?: boolean;
  csrf?: string;
}) {
  const intro = useRef<HTMLParagraphElement>(null);
  const returnButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    (returnButton.current ?? intro.current)?.focus();
  }, []);
  const [versions, setVersions] = useState<DesignPublishedSnapshot[] | null>(
    null,
  );
  const [error, setError] = useState("");
  const [hasSavedDraftChanges, setHasSavedDraftChanges] = useState(false);
  const [retry, setRetry] = useState(0);
  const [confirmation, setConfirmation] = useState<{
    version: DesignPublishedSnapshot;
    website: DesignWebsite;
    key: string;
  } | null>(null);
  const [restoration, setRestoration] = useState<DesignSaveOperation | null>(
    null,
  );
  const [restoreError, setRestoreError] = useState("");
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [posting, setPosting] = useState(false);
  const restoreLock = useRef(false);
  const restoring =
    restoration?.status === "CHECKING" || restoration?.status === "PUBLISHING";
  useEffect(() => {
    if (!canRestore) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function poll() {
      try {
        const operations = await platform<DesignSaveOperation[]>(
          `websites/${websiteId}/save-operations/`,
          { org, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const current = operations[0] ?? null;
        const latest = current?.restore_release_id ? current : null;
        setSaveInProgress(
          current?.status === "CHECKING" || current?.status === "PUBLISHING",
        );
        setRestoreError("");
        setRestoration(latest);
        if (latest?.status === "LIVE") setRetry((value) => value + 1);
        if (current?.status === "CHECKING" || current?.status === "PUBLISHING")
          timer = setTimeout(poll, 4000);
      } catch (cause) {
        if (!controller.signal.aborted)
          setRestoreError(
            cause instanceof Error
              ? cause.message
              : "Could not check restoration progress.",
          );
        if (
          !controller.signal.aborted &&
          (!(cause instanceof Error) ||
            !("status" in cause) ||
            ![401, 403, 404].includes(Number(cause.status)))
        )
          timer = setTimeout(poll, 4000);
      }
    }
    void poll();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [websiteId, org, canRestore, restoration?.id]);
  async function prepareRestore(version: DesignPublishedSnapshot) {
    if (!canRestore || hasUnsavedWork || saveInProgress || restoreLock.current)
      return;
    restoreLock.current = true;
    setPosting(true);
    setRestoreError("");
    try {
      const website = await platform<DesignWebsite>(`websites/${websiteId}/`, {
        org,
      });
      setConfirmation({ version, website, key: crypto.randomUUID() });
    } catch (cause) {
      setRestoreError(
        cause instanceof Error
          ? cause.message
          : "Could not prepare restoration.",
      );
    } finally {
      restoreLock.current = false;
      setPosting(false);
    }
  }
  async function confirmRestore() {
    if (
      !confirmation ||
      !csrf ||
      !canRestore ||
      saveInProgress ||
      hasUnsavedWork ||
      restoreLock.current
    )
      return;
    restoreLock.current = true;
    setPosting(true);
    setRestoreError("");
    try {
      const operation = await platform<DesignSaveOperation>(
        `websites/${websiteId}/save/`,
        {
          method: "POST",
          org,
          csrf,
          body: {
            request_key: confirmation.key,
            expected_source_version: confirmation.website.source_version,
            expected_live_release: confirmation.website.live_release,
            restore_release_id: confirmation.version.release_id,
          },
        },
      );
      setRestoration(operation);
      setConfirmation(null);
    } catch (cause) {
      setRestoreError(
        cause instanceof Error ? cause.message : "Could not start restoration.",
      );
    } finally {
      restoreLock.current = false;
      setPosting(false);
    }
  }

  useEffect(() => {
    const controller = new AbortController();
    setVersions(null);
    setHasSavedDraftChanges(false);
    setError("");
    Promise.all([
      platform<DesignPublishedSnapshot[]>(
        `websites/${websiteId}/publications/`,
        {
          org,
          signal: controller.signal,
        },
      ),
      canRestore && !previewOnly
        ? platform<DesignWebsite>(`websites/${websiteId}/`, {
            org,
            signal: controller.signal,
          })
        : Promise.resolve(null),
    ])
      .then(([value, website]) => {
        if (!controller.signal.aborted) {
          setVersions(value);
          setHasSavedDraftChanges(
            website?.publication_state === "unpublished_changes",
          );
        }
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error
              ? cause.message
              : "Version history could not be loaded.",
          );
      });
    return () => controller.abort();
  }, [websiteId, org, retry, canRestore, previewOnly]);
  return (
    <>
      <div className={css.historyIntro}>
        <p ref={intro} tabIndex={-1}>
          Published versions of your entire website, including all pages,
          navigation and styling.
        </p>
        {onReturnEditor && (
          <Button ref={returnButton} onClick={onReturnEditor}>
            Back to editing {pageName ?? "page"}
          </Button>
        )}
      </div>
      {hasUnsavedWork && (
        <p role="status" className={css.historyNotice}>
          Your unsaved edits and chat are kept in the editor.
        </p>
      )}
      {previewOnly && (
        <p className={css.historyNotice}>
          Live publishing and restoration are disconnected in this local
          preview.
        </p>
      )}
      {saveInProgress && !restoring && (
        <p role="status" className={css.historyNotice}>
          A website save is in progress.
        </p>
      )}
      {restoring && (
        <p role="status" className={css.historyNotice}>
          {restoration?.status === "CHECKING"
            ? "Checking restored website…"
            : "Publishing restored website…"}{" "}
          Your previous live website stays active until this succeeds.
        </p>
      )}
      {restoration?.status === "FAILED" && (
        <p role="alert" className={css.historyNotice}>
          Restoration failed. Your previous live website is unchanged. Return to
          the editor to retry the saved operation.
        </p>
      )}
      {restoreError && !confirmation && (
        <p role="alert" className={css.historyNotice}>
          {restoreError}
        </p>
      )}
      <section
        className={`${css.panel} ${css.pagesPanel}`}
        aria-label="Website version history"
      >
        {error ? (
          <div className={css.pagesEmpty} role="alert">
            {error}{" "}
            <Button onClick={() => setRetry((value) => value + 1)}>
              Retry
            </Button>
          </div>
        ) : versions === null ? (
          <p className={css.pagesEmpty} role="status">
            Loading version history…
          </p>
        ) : !versions.length ? (
          <div className={css.historyEmpty}>
            <h2>No published versions yet</h2>
            <p>
              {previewOnly
                ? "Once publishing is connected, successful publications will appear here."
                : "Successful website publications will appear here."}{" "}
              Saved drafts are separate from published versions.
            </p>
          </div>
        ) : (
          <div
            className={css.pagesTableScroll}
            role="region"
            aria-label="Version history table"
            tabIndex={0}
          >
            <table
              className={`${css.pagesTable} ${css.historyTable}`}
              aria-label="Published website versions"
            >
              <thead>
                <tr>
                  <th scope="col">Published</th>
                  <th scope="col">By</th>
                  <th scope="col">Version</th>
                  <th scope="col" aria-label="Version actions" />
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.release_id}>
                    <th scope="row">
                      <time dateTime={version.published_at}>
                        {new Date(version.published_at).toLocaleString()}
                      </time>
                    </th>
                    <td>{version.published_by ?? "Not recorded"}</td>
                    <td>
                      <span
                        className={
                          version.is_live
                            ? css.currentVersion
                            : css.previousVersion
                        }
                      >
                        {version.is_live ? "Current" : "Previous"}
                      </span>
                    </td>
                    <td className={css.pageActions}>
                      <div className={css.historyActionGroup}>
                        {version.review_url && (
                          <a
                            className={css.pageEdit}
                            href={version.review_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Preview <ExternalLink size={14} />
                          </a>
                        )}
                        {canRestore &&
                          (!version.is_live || hasSavedDraftChanges) && (
                            <Button
                              variant="secondary"
                              className={css.pageEdit}
                              disabled={
                                hasUnsavedWork ||
                                posting ||
                                restoring ||
                                saveInProgress
                              }
                              onClick={() => void prepareRestore(version)}
                              title={
                                hasUnsavedWork
                                  ? "Save or discard editor changes before restoring."
                                  : undefined
                              }
                            >
                              Restore
                            </Button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {confirmation && (
        <Dialog
          ariaLabel="Restore entire website"
          onClose={() => {
            if (!posting) setConfirmation(null);
          }}
          closeOnEscape={!posting}
        >
          <DialogHeader>
            <DialogTitle>Restore entire website?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p>
              This replaces all pages, navigation and styling with the version
              published{" "}
              {new Date(confirmation.version.published_at).toLocaleString()}.
            </p>
            <p>
              Your current saved website will be kept as a recovery checkpoint.
              The previous live website stays active until the restored version
              passes checking and publication.
            </p>
            {restoreError && <p role="alert">{restoreError}</p>}
          </DialogBody>
          <DialogFooter>
            <Button disabled={posting} onClick={() => setConfirmation(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={posting || hasUnsavedWork}
              onClick={() => void confirmRestore()}
            >
              {posting ? "Starting restoration…" : "Restore entire website"}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
}
